import { useState, useEffect, useMemo, ReactNode } from "react";
import clsx from "clsx";

// MUI
import { Box, Stack, SxProps } from "@mui/material";
import { useGridApiRef, GridColDef } from "@mui/x-data-grid-premium";
import { GridValidRowModel } from "@mui/x-data-grid-pro";

// Components
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { COLUMN_RESIZE_FACTOR } from "@/Constants/tables";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";

//Helpers
import { generateSx, getRowClassName, getPinnedCellClassName } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { getFormatDate } from "@Common/helpers/Dates";
import { tableKeys } from "@/Constants/tableKeys";

// Types
import { DataSmartTableProps } from "@/Layouts/components/types";

// Interfaces
import { ICallActivity } from "@/DataPages/types/callActivity/model";

// Icons
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
const greenText = ["Answer", "Parked", "Unparked"];
const redText = ["No Answer", "Voicemail", "Busy", "Abandoned", "Rejected", "Picked", "Park Timeout", "Redirected"];
const notUsableOutboundResults = ["Busy", "No Answer", "Failed", "Rejected"];
const yellowText = ["Failed", "Congested"];
const HEADER_CLASS_NAME = "table-header-top-aligned table-small-header show-as-column";
const TEXT_RIGHT = " !text-right";
const CELL_CLASS_NAME = "table-small-cell no-focus";
const TEXT_DISABLED_CLASS_NAME = "text-disabled";
const DEFAULT_WIDTH_ONE_HUNDRED_FORTY = 140;
const DEFAULT_WIDTH_ONE_HUNDRED_EIGHTY = 180;
const DEFAULT_WIDTH_TWO_HUNDRED_TEN = 210;

const toMinutesAndSeconds = (input: number) => {
	const minutes = Math.floor(input / 60).toString();
	let seconds = (input % 60).toString();
	if (seconds.length === 1) {
		seconds = "0" + seconds;
	}
	return `${minutes}:${seconds}`;
};
const pinnedColumns = { left: ["userId", "externalPhone", "callDate", "callTime", "queue", "result", "__empty"] };

const columnsVisibilityModel = {
	_id: false,
};

const withCircle = (child: ReactNode, isAlert = false) => {
	return (
		<Stack direction={"row"} alignItems={"center"}>
			<CircleRoundedIcon className={clsx("!text-[12px] mr-[7px] text-transparent", { "!text-alert-color": isAlert })} />
			{child}
		</Stack>
	);
};

const getCellClassName = (params: GridValidRowModel) => {
	if (!params?.row?.isUsable) {
		return "!text-disabled";
	}
	return "";
};

const getRowId = (row: GridValidRowModel) => row._id;

const getUnusableReasonInbound = ({ isValidCallNumber, isValidCallDate, isQueue, isLastEntryOfCallId }: ICallActivity) => {
	switch (true) {
		case !isValidCallNumber:
			return "CALL_NUMBER";
		case !isValidCallDate:
			return "CALL_DATE";
		case isQueue && !isLastEntryOfCallId:
			return "QUEUE";
		default:
			return "CALL_ID";
	}
};

const getUnusableReasonOutbound = ({
	isValidRingDuration,
	isValidTalkDuration,
	isValidHoldDuration,
	isValidTotalDuration,
	result,
	isLastEntryOfCallId,
}: ICallActivity) => {
	switch (true) {
		case !isLastEntryOfCallId:
			return "CALL_ID";
		case !isValidRingDuration:
			return "RING_DURATION";
		case !isValidTalkDuration:
			return "TALK_DURATION";
		case !isValidHoldDuration:
			return "HOLD_DURATION";
		case !isValidTotalDuration:
			return "TOTAL_DURATION";
		case notUsableOutboundResults.includes(result):
			return "RESULT";
		default:
			return "CALL_ID";
	}
};

const getUnusableReason = (row: ICallActivity & { isUsable: boolean }) => {
	if (row.isUsable) {
		return "";
	}

	if (row.callType === "Inbound") {
		return getUnusableReasonInbound(row);
	}
	return getUnusableReasonOutbound(row);
};

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const customProps: DataSmartTableProps["customProps"] = {
	tableContainerClassName: "[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
	tableClassName: {
		"functional-table": true,
		"[&_.toolbar-actions]:!right-0": true,
		"[&_.toolbar-actions]:!relative [&_.toolbar-actions]:!top-0 [&_.toolbar-actions]:!right-[-12px]": true,
		"[&_.toolbar-actions]:!mb-[12px]": true,
		"[&_.MuiDataGrid-iconSeparator]:!mt-[0px]": true,
		"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
		"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
		"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row))_.MuiDataGrid-cell--pinnedLeft:not(.empty-row)]:shadow-table-line": true,
		"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
		"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row)):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-line-hov":
			true,
	},
	expandableRows: false,
	hide: false,
};

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "ID",
		minWidth: 10,
		width: 10,
		filterable: false,
		resizable: false,
	},
	{
		field: "userId",
		headerName: "User ID",
		minWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		width: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		maxWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
		renderCell: (row) => {
			const isVoiceMail = row?.row?.isVoiceMail;
			const userId = row?.row?.userId || "";
			return withCircle(isVoiceMail ? "Voicemail" : userId, false); //getUnusableReason(params.row) === "USER_ID");
		},
	},
	{
		field: "externalPhone",
		headerName: "External Phone",
		minWidth: 110,
		width: 110,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (param) => getPinnedCellClassName(param, "table-small-cell no-focus table-noWrap"),
		renderCell: (params) =>
			withCircle(
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_FORTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>,
				getUnusableReason(params.row) === "CALL_NUMBER"
			),
		resizable: false,
	},
	{
		field: "callDate",
		headerName: "Call Date",
		minWidth: 95,
		width: 95,
		type: "date",
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
		valueGetter: (_, row) => new Date(row?.timestamp),
		renderCell: (params) =>
			withCircle(
				<CustomCell
					value={getFormatDate(params.value, DATE_FORMAT.monthAbbrDatePadded) || ""}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_FORTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>,
				getUnusableReason(params.row) === "CALL_DATE"
			),
		resizable: false,
	},
	{
		field: "callTime",
		headerName: "Call Time",
		minWidth: 70,
		width: 70,
		headerClassName: HEADER_CLASS_NAME,
		resizable: false,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
		valueGetter: (_, row) => getFormatDate(new Date(row?.timestamp), DATE_FORMAT.time12h),
		renderCell: (row) => {
			return <>{row.value}</>;
		},
	},
	{
		field: "queue",
		headerName: "Queue",
		minWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		width: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		maxWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
		renderCell: (params) =>
			withCircle(
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_FORTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>,
				getUnusableReason(params.row) === "QUEUE"
			),
	},
	{
		field: "result",
		headerName: "Result",
		minWidth: 90,
		width: 90,
		headerClassName: HEADER_CLASS_NAME,
		resizable: false,
		cellClassName: (params) => {
			return getPinnedCellClassName(params);
		},
		renderCell: (params) => {
			const cellValue = String(params.value);
			const unevaluated = !params.row.isUsable;
			const classes = `${CELL_CLASS_NAME} !${clsx({
				TEXT_DISABLED_CLASS_NAME:
					(greenText.includes(cellValue) || yellowText.includes(cellValue) || redText.includes(cellValue)) && unevaluated,
				"text-success": greenText.includes(cellValue) && !unevaluated,
				"text-alert-color": redText.includes(cellValue) && !unevaluated,
				"text-warning": yellowText.includes(cellValue) && !unevaluated,
			})}`;
			return withCircle(
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? 70}
					fontSize="12px"
					classes={params.row.isUsable ? classes : classes + " text-disabled"}
				/>,
				getUnusableReason(params.row) === "RESULT"
			);
		},
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		minWidth: 12,
		width: 12,
		maxWidth: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-blue"} />,
	},
	{
		field: "vmUserId",
		headerName: "VM User ID",
		sortable: true,
		minWidth: 100,
		width: 100,
		maxWidth: 100 * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		valueGetter: (_value, row) => (row?.isVoiceMail ? row?.userId ?? "" : ""),
		renderCell: (row) => {
			if (row?.row?.isVoiceMail) {
				return row?.row?.userId || "";
			} else {
				return "";
			}
		},
	},
	{
		field: "extension",
		headerName: "Extn",
		minWidth: 90,
		width: 90,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		resizable: false,
		renderCell: (params) => {
			return (
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? 90}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>
			);
		},
	},
	{
		field: "callType",
		headerName: "Call Type",
		minWidth: 75,
		width: 75,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		resizable: false,
		renderCell: (params) => {
			return (
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? 75}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>
			);
		},
	},
	{
		field: "customerName",
		headerName: "First Matching DMS Cust",
		minWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		width: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		maxWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		renderCell: (params) => {
			return (
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_FORTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>
			);
		},
	},
	{
		field: "dialedNumberName",
		headerName: "Dialed Number Name",
		minWidth: DEFAULT_WIDTH_ONE_HUNDRED_EIGHTY,
		width: DEFAULT_WIDTH_ONE_HUNDRED_EIGHTY,
		maxWidth: DEFAULT_WIDTH_ONE_HUNDRED_EIGHTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		renderCell: (params) => {
			return (
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_EIGHTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>
			);
		},
	},
	{
		field: "dialedNumber",
		headerName: "Dialed Number",
		minWidth: 100,
		width: 100,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		resizable: false,
		renderCell: (params) => {
			return (
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? 100}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>
			);
		},
	},
	{
		field: "callerIdName",
		headerName: "Caller ID Name",
		minWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		width: DEFAULT_WIDTH_ONE_HUNDRED_FORTY,
		maxWidth: DEFAULT_WIDTH_ONE_HUNDRED_FORTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		renderCell: (params) => {
			return (
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_FORTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>
			);
		},
	},
	{
		field: "ringDuration",
		headerName: "Ring Duration",
		minWidth: 75,
		width: 75,
		headerClassName: HEADER_CLASS_NAME + TEXT_RIGHT,
		cellClassName: CELL_CLASS_NAME + TEXT_RIGHT,
		renderCell: (params) => withCircle(toMinutesAndSeconds(params.value), getUnusableReason(params.row) === "RING_DURATION"),
		resizable: false,
	},
	{
		field: "talkDuration",
		headerName: "Talk Duration",
		minWidth: 75,
		width: 75,
		headerClassName: HEADER_CLASS_NAME + TEXT_RIGHT,
		cellClassName: CELL_CLASS_NAME + TEXT_RIGHT,
		renderCell: (params) => withCircle(toMinutesAndSeconds(params.value), getUnusableReason(params.row) === "TALK_DURATION"),
		resizable: false,
	},
	{
		field: "holdDuration",
		headerName: "Hold Duration",
		minWidth: 75,
		width: 75,
		headerClassName: HEADER_CLASS_NAME + TEXT_RIGHT,
		cellClassName: CELL_CLASS_NAME + TEXT_RIGHT,
		renderCell: (params) => withCircle(toMinutesAndSeconds(params.value), getUnusableReason(params.row) === "HOLD_DURATION"),
		resizable: false,
	},
	{
		field: "totalDuration",
		headerName: "Total Duration",
		minWidth: 75,
		width: 75,
		headerClassName: HEADER_CLASS_NAME + TEXT_RIGHT,
		cellClassName: CELL_CLASS_NAME + TEXT_RIGHT,
		renderCell: (params) => withCircle(toMinutesAndSeconds(params.value), getUnusableReason(params.row) === "TOTAL_DURATION"),
		resizable: false,
	},
	{
		field: "recorded",
		headerName: "Recorded",
		minWidth: 80,
		width: 80,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		resizable: false,
		renderCell: (row) => {
			return row.value ? "Yes" : "No";
		},
	},
	{
		field: "callId",
		headerName: "Call ID",
		minWidth: DEFAULT_WIDTH_TWO_HUNDRED_TEN,
		width: DEFAULT_WIDTH_TWO_HUNDRED_TEN,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: CELL_CLASS_NAME,
		renderCell: (params) =>
			withCircle(
				<CustomCell
					value={params.value}
					hasTooltip
					maxWith={params.colDef.width ?? DEFAULT_WIDTH_ONE_HUNDRED_FORTY}
					fontSize="12px"
					classes={params.row.isUsable ? "" : TEXT_DISABLED_CLASS_NAME}
				/>,
				getUnusableReason(params.row) === "CALL_ID"
			),
	},
];

interface CallActivityReportTableProps {
	data: ICallActivity[];
	hasNextPage: boolean;
}

export default function CallActivityReportTable(props: Readonly<CallActivityReportTableProps>) {
	const { data, hasNextPage } = props;
	const apiRef = useGridApiRef();
	const [sx, setSx] = useState<SxProps>({});
	const [filterOutUnevaluatedCalls, setFilterOutUnevaluatedCalls] = useState(false);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
				switch: {
					onChange: () => {
						setFilterOutUnevaluatedCalls(!filterOutUnevaluatedCalls);
					},
					sx: { margin: "16px -2px" },
					checked: filterOutUnevaluatedCalls,
					disabled: false,
					searchValue: true,
					searchIn: "isUsable",
					textRight: true,
					textChecked: "Filter Out Unevaluated Calls",
					textUnChecked: "Filter Out Unevaluated Calls",
					data,
				},
				customComponent: (
					<Box className="mb-4" hidden={!hasNextPage}>
						<LoadingSpinner label="Call Activity Report is still loading..." className="items-center" />
					</Box>
				),
			},
			noRowsOverlay: {
				title: "No Call Activity Report Data",
				className: "text-montserrat",
			},
			noResultsOverlay: {
				title: "No Results Found",
				className: "!text-montserrat",
			},
		}),
		[filterOutUnevaluatedCalls, hasNextPage, data]
	);

	useEffect(() => {
		setSx(generateSx(apiRef));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [props, apiRef]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.CALL_ACTIVITY,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={data}
			maxRows={19}
			enableCustomScrollbars
			hideFooter
			disableColumnMenu
			getRowClassName={getRowClassName}
			getRowId={getRowId}
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={columnsVisibilityModel}
			getCellClassName={getCellClassName}
			slotProps={slotProps}
			pinnedColumns={pinnedColumns}
			slots={slots}
			customProps={customProps}
			sx={sx}
		/>
	);
}
